import { useRef } from "react";

import PropTypes from "prop-types";
import { compose } from "recompose";
import { connect } from "react-redux";
import { Field, reduxForm, isValid } from "redux-form";
import { Box, Button, Grid, MenuItem, Typography } from "@material-ui/core";

import SelectItem from "../../../../components/SelectItem/SelectItem";
import SelectWithTitle from "../../../../components/SelectWithTitle/SelectWithTitle";
import { CUSTOMER_SETTINGS_BULK_CHAT_QUEUE_FORM } from "../../../../constants/forms";
import {
  APPLY,
  BULK_ACCOUNT_NUMBERS,
  BULK_ACCOUNT_NUMBERS_HINT,
  BULK_ADD_QUEUE_TYPE,
  BULK_CHAT_QUEUE_CSV_HINT,
  QUEUE_TYPE,
  SERVICE_SELECT_DEFAULT,
  UPLOAD_CSV,
} from "../../../../constants/strings";
import {
  onConfirmBulkSaveChatQueue,
  onUploadBulkChatQueueCsv,
} from "../../../../features/customerSettings/customerSettings.action";
import {
  BULK_ACCOUNTS_FIELD,
  CHAT_QUEUE_TYPES,
  QUEUE_TYPE_FIELD,
  QUEUE_TYPES,
} from "../../../../features/customerSettings/customerSettings.constants";
import {
  getInitialBulkChatQueueFormValues,
  validateBulkChatQueueFormValues,
} from "../../../../features/customerSettings/customerSettings.models";
import withErrorTooltip from "../../../../hocs/withErrorTooltip";
import AccountChipField from "../AccountChipField";
import styles from "./BulkChatQueueForm.module.scss";

const DecoratedAccountChipField = withErrorTooltip(AccountChipField);
const DecoratedSelectWithError = withErrorTooltip(SelectWithTitle);

const BulkChatQueueForm = ({
  handleSubmit,
  isFormValid,
  onApply,
  onUploadCsv,
}) => {
  const fileInputRef = useRef(null);

  const openCsvPicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleCsvSelected = (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";

    if (file) {
      onUploadCsv(file);
    }
  };

  return (
    <Box display="flex" flexDirection="column" width="100%" mb={2}>
      <Box display="flex" flexDirection="row" width="100%" mb={2} mt={4}>
        <Typography className={styles["title-text"]}>
          {BULK_ADD_QUEUE_TYPE}:
        </Typography>
      </Box>
      <Typography className={styles.hint}>
        {BULK_ACCOUNT_NUMBERS_HINT}
      </Typography>
      <Typography className={styles.hint}>
        {BULK_CHAT_QUEUE_CSV_HINT}
      </Typography>
      <Grid container spacing={3} alignItems="flex-end">
        <Grid item xs={12}>
          <Field
            name={BULK_ACCOUNTS_FIELD}
            component={DecoratedAccountChipField}
            label={BULK_ACCOUNT_NUMBERS}
            showMetaErrors
          />
        </Grid>
        <Grid item xs={12} md className={styles.queueTypeItem}>
          <Field
            component={DecoratedSelectWithError}
            name={QUEUE_TYPE_FIELD}
            label={QUEUE_TYPE}
            displayEmpty
            showMetaErrors
            containerStyles={styles.queueType}
          >
            <MenuItem value={QUEUE_TYPES.NONE} disabled>
              <SelectItem title={SERVICE_SELECT_DEFAULT} />
            </MenuItem>
            {CHAT_QUEUE_TYPES.map((queueType) => (
              <MenuItem key={queueType} value={queueType}>
                <SelectItem title={queueType} />
              </MenuItem>
            ))}
          </Field>
        </Grid>
        <Grid item className={styles.uploadItem}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            aria-label={UPLOAD_CSV}
            className={styles.fileInput}
            onChange={handleCsvSelected}
          />
          <Button disableElevation onClick={openCsvPicker}>
            <span className={styles.buttonText}>{UPLOAD_CSV}</span>
          </Button>
        </Grid>
        <Grid item className={styles.applyItem}>
          <Button
            className={styles.applyButton}
            disableElevation
            onClick={handleSubmit(onApply)}
            disabled={!isFormValid}
          >
            <span className={styles.buttonText}>{APPLY}</span>
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

BulkChatQueueForm.propTypes = {
  handleSubmit: PropTypes.func,
  isFormValid: PropTypes.bool,
  onApply: PropTypes.func,
  onUploadCsv: PropTypes.func,
};

const mapStateToProps = (state) => ({
  isFormValid: isValid(CUSTOMER_SETTINGS_BULK_CHAT_QUEUE_FORM)(state),
});

const mapDispatchToProps = (dispatch) => ({
  onApply: () => dispatch(onConfirmBulkSaveChatQueue()),
  onUploadCsv: (file) => dispatch(onUploadBulkChatQueueCsv(file)),
});

export default compose(
  reduxForm({
    form: CUSTOMER_SETTINGS_BULK_CHAT_QUEUE_FORM,
    initialValues: getInitialBulkChatQueueFormValues(),
    validate: validateBulkChatQueueFormValues,
  }),
  connect(mapStateToProps, mapDispatchToProps)
)(BulkChatQueueForm);
